import { useEffect } from 'react';

/**
 * Below this, a shrinking viewport is browser chrome (the URL bar collapsing),
 * not a keyboard. Every soft keyboard is far taller than 120px.
 */
const KEYBOARD_MIN_PX = 120;

/**
 * Publishes the VISIBLE viewport to CSS: `--app-height`, `--keyboard-inset`,
 * and `data-keyboard="open" | "closed"` on `<html>`.
 *
 * `100dvh` follows the browser's own chrome and never the software keyboard, so
 * on a phone the terminal keeps its full height while the keyboard covers the
 * bottom third of it. Because the grid is `overflow: hidden`, whatever the
 * keyboard covers is simply unreachable, and iOS's attempt to scroll a focused
 * field into view leaves the whole app parked at an offset with no way back —
 * which is what "stuck" looked like when tapping the voucher field.
 *
 * Chrome fixes this from the viewport meta (`interactive-widget=resizes-content`
 * in index.html); iOS ignores that hint, so here we measure and let CSS size to
 * the result.
 *
 * Mount it on surfaces that own the whole screen and do not scroll — the
 * terminal does; the landing page must NOT, since its own scrolling would fight
 * the reset below.
 */
export function useViewportInsets(): void {
    useEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport) return;

        const root = document.documentElement;

        const apply = () => {
            const height = Math.round(viewport.height);
            // A zero reading (mid-rotation on some browsers) would collapse the
            // layout; leave the last good value in place.
            if (height <= 0) return;

            // What the keyboard eats: the layout viewport minus the visible one,
            // minus however far the visible one has been scrolled down it.
            const inset = Math.max(
                0,
                Math.round(
                    window.innerHeight - viewport.height - viewport.offsetTop,
                ),
            );
            const open = inset >= KEYBOARD_MIN_PX;

            root.style.setProperty('--app-height', `${height}px`);
            root.style.setProperty('--keyboard-inset', `${inset}px`);
            root.dataset.keyboard = open ? 'open' : 'closed';

            // iOS scrolls the WINDOW to reveal a focused field. Sizing to the
            // visible viewport already put that field on screen, so the scroll
            // is pure damage — and on a non-scrolling grid the user cannot undo
            // it. Put it back once the keyboard is gone.
            if (!open && window.scrollY !== 0) window.scrollTo(0, 0);
        };

        apply();
        viewport.addEventListener('resize', apply);
        viewport.addEventListener('scroll', apply);

        return () => {
            viewport.removeEventListener('resize', apply);
            viewport.removeEventListener('scroll', apply);
            root.style.removeProperty('--app-height');
            root.style.removeProperty('--keyboard-inset');
            delete root.dataset.keyboard;
        };
    }, []);
}
